import { FormControl, FormHelperText, FormLabel, Input, Stack, Typography } from '@healthinal/ui';
import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { AddressDtoAddressType } from '../../../api/generated/model';
import { FileInput } from '../../../components/form/FileInput.tsx';
import { GermanDateInput } from '../../../components/form/GermanDateInput.tsx';
import { InstitutionContactFields } from '../../../components/form/InstitutionContactFields.tsx';
import { SelectUser } from '../SelectUser.tsx';

interface LabOrderDetailsFormProps<TFieldValues extends FieldValues> {
  samplingDate: FieldPath<TFieldValues>;
  samplingTime: FieldPath<TFieldValues>;
  isEditing: boolean;
  file?: FieldPath<TFieldValues>;
  fileInputName?: string;
  fileInputPlaceHolder?: string;
  fileInputMimeType?: string;
  isFileInputDisabled?: boolean;
  samplingUser?: FieldPath<TFieldValues>;
  samplingUserInputName?: string;
  isSamplingUserDisabled?: boolean;
  samplingAddress?: FieldPath<TFieldValues>;
  samplingContact?: FieldPath<TFieldValues>;
  addressTypes?: AddressDtoAddressType[];
  isSamplingAddressDisabled?: boolean;
  isSamplingContactDisabled?: boolean;
}
export function LabOrderDetailsForm<TFieldValues extends FieldValues>({
  samplingDate,
  samplingTime,
  samplingUser,
  file,
  samplingUserInputName,
  isSamplingUserDisabled = false,
  samplingAddress,
  samplingContact,
  addressTypes = Object.values(AddressDtoAddressType),
  isSamplingAddressDisabled = false,
  isSamplingContactDisabled = false,
  fileInputName,
  fileInputPlaceHolder,
  fileInputMimeType,
  isFileInputDisabled = false,
  isEditing = false,
}: LabOrderDetailsFormProps<TFieldValues>) {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<TFieldValues>();

  return (
    <>
      <Stack direction={'row'} gap={2}>
        <FormControl sx={{ flex: 1 }} error={!!errors.samplingDate}>
          <FormLabel>
            <Typography>Datum *</Typography>
          </FormLabel>
          <GermanDateInput
            control={control}
            name={samplingDate}
            rules={{
              required: true,
            }}
            disabled={isEditing}
          />
          {errors.samplingDate && <FormHelperText>Datum muss ausgefüllt werden</FormHelperText>}
        </FormControl>
        <FormControl sx={{ flex: 1 }} error={!!errors.samplingTime}>
          <FormLabel>
            <Typography>Zeit</Typography>
          </FormLabel>
          <Input {...register(samplingTime)} type={'time'} disabled={isEditing} />
          {errors.samplingTime && <FormHelperText>Zeit muss ausgefüllt werden</FormHelperText>}
        </FormControl>
      </Stack>
      {samplingAddress && samplingContact && (
        <InstitutionContactFields
          addressField={samplingAddress}
          contactField={samplingContact}
          addressTypes={addressTypes}
          isAddressDisabled={isSamplingAddressDisabled || isEditing}
          isContactDisabled={isSamplingContactDisabled || isEditing}
        />
      )}
      {(samplingUser || file) && (
        <Stack direction={'row'} gap={2}>
          {samplingUser && samplingUserInputName && (
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>
                <Typography>{samplingUserInputName}</Typography>
              </FormLabel>
              <Controller
                name={samplingUser}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <SelectUser onChange={onChange} value={value} disabled={isSamplingUserDisabled} />
                )}
              />
            </FormControl>
          )}
          {file && fileInputName && fileInputMimeType && (
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>
                <Typography>{fileInputName}</Typography>
              </FormLabel>
              <Controller
                name={file}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FileInput
                    value={value}
                    onChange={onChange}
                    accept={fileInputMimeType}
                    placeholder={fileInputPlaceHolder ?? ''}
                    disabled={isFileInputDisabled}
                  />
                )}
              />
            </FormControl>
          )}
        </Stack>
      )}
    </>
  );
}
